import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { APP_CONFIG } from '../../config/appConfig';
import { linkPhoneToUid, getUserByPhone, linkSocialToExistingUser, initUserDoc, getUserProfileByUid } from '../../services/userProfileService';
import { getLastSocialProvider } from '../../services/authService';
import { toE164KR, genOtp6, sendOtpViaProxy } from '../../services/recoveryService';

function normalizePhone(raw) {
  return raw.replace(/[^0-9]/g, "");
}

function isTestNumber(phone) {
  const n = normalizePhone(phone);
  const { start, end } = APP_CONFIG.testRange;
  return n >= start && n <= end;
}

const LinkPhonePage = () => {
  const navigate = useNavigate();
  const { currentUser, refreshUser, logout } = useAuth();

  const [phone, setPhone] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [serverCode, setServerCode] = useState('');
  const [testCode, setTestCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [timer, setTimer] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const startTimer = () => {
    setTimer(180);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) { clearInterval(intervalRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    const normalized = normalizePhone(phone);
    if (normalized.length < 10) {
      setError('올바른 전화번호를 입력해주세요.');
      return;
    }
    setBusy(true);
    setError('');

    if (isTestNumber(normalized)) {
      const otp = genOtp6();
      setTestCode(otp);
      setServerCode(otp);
      setCodeSent(true);
      startTimer();
      setBusy(false);
      return;
    }

    const otp = genOtp6();
    setServerCode(otp);

    try {
      await sendOtpViaProxy({ phone: normalized, authcode: otp });
      setCodeSent(true);
      startTimer();
    } catch (err) {
      setError(err.message || 'SMS 발송에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    if (!code.trim()) {
      setError('인증번호를 입력해주세요.');
      return;
    }
    if (serverCode && code.trim() !== serverCode) {
      // OTP 틀렸을 때 코드/타이머 초기화 (jogunBiz 패턴)
      setError('인증번호가 일치하지 않습니다.');
      setCode('');
      setServerCode('');
      setCodeSent(false);
      setTestCode('');
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimer(0);
      return;
    }

    setBusy(true);
    setError('');

    const phoneE164 = toE164KR(phone);
    const provider = getLastSocialProvider();

    try {
      // 이 번호로 이미 등록된 기존 유저가 있는지 확인
      const existingUser = await getUserByPhone(phoneE164);

      if (existingUser && existingUser.uid !== currentUser.uid) {
        // 기존 계정 발견 → 소셜 계정을 기존 계정에 연결
        await linkSocialToExistingUser({
          existingUid: existingUser.uid,
          socialUid: currentUser.uid,
          provider: provider,
        });
        await linkPhoneToUid({ uid: existingUser.uid, phoneE164 });
      } else {
        // 기존 계정 없음 → 현재 계정에 문서 생성 후 전화번호 연결
        const profile = await getUserProfileByUid(currentUser.uid);
        const firestoreDocId = profile?.uid || currentUser.uid;

        // 프로필 문서가 없으면 initUserDoc으로 생성
        if (!profile || (!profile.name && !profile.role && !profile.phoneE164)) {
          await initUserDoc({
            uid: firestoreDocId,
            email: currentUser.email,
            provider: provider || 'unknown',
          });
        }

        await linkPhoneToUid({ uid: firestoreDocId, phoneE164 });
      }

      // 전화번호로 학원에 등록된 자녀·학원 자동 연결.
      // ⚠️ 백그라운드(fire-and-forget)로 호출 — 이 요청이 느리거나 실패해도
      //    인증 완료 → 화면 전환을 절대 막지 않게 한다. (await 하지 않음)
      currentUser.getIdToken()
        .then((idToken) => fetch(APP_CONFIG.resolveParentLinksUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken }),
        }))
        .catch((mapErr) => console.warn('자동매핑 호출 실패(무시):', mapErr));

      await refreshUser();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('전화번호 연동 실패:', err);
      setError('전화번호 인증에 실패했습니다. (' + (err.message || err.code || '') + ')');
    } finally {
      setBusy(false);
    }
  };

  // 아직 가입(전화인증) 미완 상태 → 카카오 세션 정리하고 로그인 화면으로
  // ⚠️ navigate 를 먼저: /app/login 은 RequireAuth 가드 밖이라, 먼저 이동해두면
  //    logout() 으로 비로그인이 돼도 가드가 /login(옛 화면)으로 튕기지 않음.
  const handleBackToStart = async () => {
    navigate('/app/login', { replace: true });
    await logout();
  };

  // 화면 숫자 키패드 — 기기 기본 키보드 없이 5자리 입력
  const pressKey = (n) => {
    setError('');
    setCode((prev) => (prev.length >= 5 ? prev : prev + n));
  };
  const pressBack = () => {
    setError('');
    setCode((prev) => prev.slice(0, -1));
  };
  // 문자에서 복사한 인증번호 붙여넣기 (숫자만 뽑아 앞 5자리)
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const digits = (text.match(/\d/g) || []).join('').slice(0, 5);
      if (digits) { setError(''); setCode(digits); }
      else setError('복사된 인증번호가 없어요.');
    } catch {
      setError('붙여넣기가 안 돼요. 키패드로 입력해 주세요.');
    }
  };

  const formatTimer = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <Container>
      <Title>전화번호 인증</Title>
      <Desc>전화번호 인증이 필요합니다.</Desc>

      <FormArea>
        <Label>전화번호</Label>
        <Input
          type="tel"
          placeholder=""
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={codeSent}
        />
        {!codeSent && (
          <SendBtn onClick={handleSendCode} disabled={busy}>
            {busy ? '전송 중...' : '인증번호 전송'}
          </SendBtn>
        )}

        {codeSent && (
          <>
            <OtpHead>
              <Label>인증번호 {timer > 0 && <TimerText>{formatTimer(timer)}</TimerText>}</Label>
              <PasteBtn type="button" onClick={handlePaste}>문자에서 붙여넣기</PasteBtn>
            </OtpHead>
            <OtpRow>
              {[0, 1, 2, 3, 4].map((i) => (
                <OtpCell key={i} $filled={i < code.length} $active={i === code.length}>
                  {code[i] || ''}
                </OtpCell>
              ))}
            </OtpRow>
            {testCode && <TestCodeBox>테스트 코드: {testCode}</TestCodeBox>}

            <Keypad>
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((n) => (
                <KeyBtn key={n} type="button" onClick={() => pressKey(n)}>{n}</KeyBtn>
              ))}
              <KeySpacer />
              <KeyBtn type="button" onClick={() => pressKey('0')}>0</KeyBtn>
              <KeyBtn type="button" onClick={pressBack} aria-label="지우기">⌫</KeyBtn>
            </Keypad>

            {error && <ErrorText>{error}</ErrorText>}

            <VerifyBtn onClick={handleVerify} disabled={busy || code.length < 5}>
              {busy ? '인증 중...' : '인증 완료'}
            </VerifyBtn>
          </>
        )}

        {!codeSent && error && <ErrorText>{error}</ErrorText>}

        <SkipBtn onClick={handleBackToStart}>
          처음으로 돌아가기
        </SkipBtn>
      </FormArea>
    </Container>
  );
};

export default LinkPhonePage;

const Container = styled.div`
  min-height: 100vh;
  background: #fff;
  padding: 80px 24px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.p`
  font-size: 22px;
  text-align: center;
  margin-bottom: 8px;
  color: #1a1a1a;
`;

const Desc = styled.p`
  font-size: 15px;
  color: #888;
  text-align: center;
  margin-bottom: 40px;
`;

const FormArea = styled.div`
  width: 100%;
  max-width: 480px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 10px;
  margin-top: 20px;
  color: #333;
`;

const TimerText = styled.span`
  color: #7C3AED;
  font-variant-numeric: tabular-nums;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 18px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 16px;
  &:focus { border-color: #7C3AED; outline: none; }
  &:disabled { background: #f5f5f5; }
`;

const SendBtn = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 12px;
  border-radius: 10px;
  background: #7C3AED;
  color: white;
  font-size: 15px;
  &:disabled { opacity: 0.5; }
`;

const VerifyBtn = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 24px;
  border-radius: 12px;
  background: #7C3AED;
  color: white;
  font-size: 16px;
  &:disabled { opacity: 0.5; }
`;

/* 인증번호 라벨 줄 (타이머 + 붙여넣기 버튼) */
const OtpHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  & > label { margin-top: 0; }
`;

const PasteBtn = styled.button`
  background: none;
  border: none;
  color: #7C3AED;
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  padding: 2px 4px;
`;

/* 인증번호 5칸 박스 (기기 키보드 없이 키패드/붙여넣기로 입력) */
const OtpRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const OtpCell = styled.div`
  flex: 1;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  background: #fff;
  border: 1.5px solid ${({ $active, $filled }) => ($active ? '#7C3AED' : $filled ? '#C4B5FD' : '#E2E5EA')};
  border-radius: 10px;
  box-shadow: ${({ $active }) => ($active ? '0 0 0 3px rgba(124, 58, 237, 0.12)' : 'none')};
  transition: border-color 0.15s, box-shadow 0.15s;
`;

const Keypad = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 24px;
`;

const KeyBtn = styled.button`
  height: 60px;
  border: none;
  border-radius: 12px;
  background: #EDEFF2;
  color: #1a1a1a;
  font-size: 22px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  &:active { background: #DDE0E5; }
`;

const KeySpacer = styled.div``;

const SkipBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  background: none;
  color: #999;
  font-size: 14px;
  text-decoration: underline;
`;

const TestCodeBox = styled.div`
  margin-top: 10px;
  padding: 12px;
  background: #FFF3E0;
  border-radius: 10px;
  font-size: 14px;
  color: #E65100;
  text-align: center;
`;

const ErrorText = styled.p`
  color: #EF5350;
  font-size: 14px;
  margin-top: 10px;
`;
